import { useState, useCallback, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useNavigate, useSearchParams } from "react-router-dom";
import { APIProvider, Map as GoogleMap, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent } from "../components/ui/card";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../components/ui/sheet";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription
} from "../components/ui/dialog";
import { 
  MapPin, Search, User, Library, Headphones, Settings,
  Play, Loader2, Volume2,
  CreditCard, Menu, Landmark, TreePine, Building2, GalleryVerticalEnd, LogIn, Package,
  Church, Castle, BookOpen, Drama, Trophy, Star, Filter, Heart, Navigation, Compass, Globe
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import UserAvatar from "../components/UserAvatar";
import { contentApi, userApi } from "../lib/api";
import api from "../lib/api";
import { toast } from "sonner";
import { addBreadcrumb, captureError, captureMessage } from "../lib/sentry";

const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
const GOOGLE_MAPS_MAP_ID = process.env.REACT_APP_GOOGLE_MAPS_MAP_ID;
let reportedMissingGoogleMapsKey = false;
let reportedMissingGoogleMapsMapId = false;

const FALLBACK_AUDIO_LANGUAGES = [
  { code: "en", label: "English" },
  { code: "es", label: "Spanish" },
  { code: "fr", label: "French" },
  { code: "de", label: "German" },
  { code: "it", label: "Italian" },
  { code: "ja", label: "Japanese" },
  { code: "zh", label: "Chinese" },
  { code: "pt", label: "Portuguese" },
];

// Category icons and colors
const CATEGORY_CONFIG = {
  landmark: { icon: Star, color: "bg-amber-500", label: "Landmarks" },
  museum: { icon: Landmark, color: "bg-indigo-500", label: "Museums & Galleries" },
  historical: { icon: Castle, color: "bg-stone-600", label: "Historical Sites" },
  natural: { icon: TreePine, color: "bg-emerald-500", label: "Parks & Gardens" },
  religious: { icon: Church, color: "bg-purple-600", label: "Churches & Temples" },
  library: { icon: BookOpen, color: "bg-cyan-600", label: "Libraries" },
  theater: { icon: Drama, color: "bg-rose-500", label: "Theatres" },
  stadium: { icon: Trophy, color: "bg-orange-600", label: "Stadiums & Arenas" },
  cultural: { icon: MapPin, color: "bg-blue-500", label: "Cultural" },
};

const ALL_FILTER_CATEGORIES = Object.keys(CATEGORY_CONFIG);

// Country center coordinates (major countries)
const COUNTRY_CENTERS = {
  "Afghanistan":{lat:33.93,lng:67.71},"Albania":{lat:41.15,lng:20.17},"Algeria":{lat:28.03,lng:1.66},"Argentina":{lat:-38.42,lng:-63.62},"Australia":{lat:-25.27,lng:133.78},"Austria":{lat:47.52,lng:14.55},"Bangladesh":{lat:23.68,lng:90.36},"Belgium":{lat:50.50,lng:4.47},"Bolivia":{lat:-16.29,lng:-63.59},"Brazil":{lat:-14.24,lng:-51.93},"Bulgaria":{lat:42.73,lng:25.49},"Cambodia":{lat:12.57,lng:105.00},"Canada":{lat:56.13,lng:-106.35},"Chile":{lat:-35.68,lng:-71.54},"China":{lat:35.86,lng:104.20},"Colombia":{lat:4.57,lng:-74.30},"Costa Rica":{lat:9.75,lng:-83.75},"Croatia":{lat:45.10,lng:15.20},"Cuba":{lat:21.52,lng:-77.78},"Cyprus":{lat:35.13,lng:33.43},"Czech Republic":{lat:49.82,lng:15.47},"Denmark":{lat:56.26,lng:9.50},"Dominican Republic":{lat:18.74,lng:-70.16},"Ecuador":{lat:-1.83,lng:-78.18},"Egypt":{lat:26.82,lng:30.80},"Estonia":{lat:58.60,lng:25.01},"Ethiopia":{lat:9.15,lng:40.49},"Finland":{lat:61.92,lng:25.75},"France":{lat:46.23,lng:2.21},"Georgia":{lat:42.32,lng:43.36},"Germany":{lat:51.17,lng:10.45},"Ghana":{lat:7.95,lng:-1.02},"Greece":{lat:39.07,lng:21.82},"Guatemala":{lat:15.78,lng:-90.23},"Honduras":{lat:15.20,lng:-86.24},"Hungary":{lat:47.16,lng:19.50},"Iceland":{lat:64.96,lng:-19.02},"India":{lat:20.59,lng:78.96},"Indonesia":{lat:-0.79,lng:113.92},"Iran":{lat:32.43,lng:53.69},"Iraq":{lat:33.22,lng:43.68},"Ireland":{lat:53.14,lng:-7.69},"Israel":{lat:31.05,lng:34.85},"Italy":{lat:41.87,lng:12.57},"Jamaica":{lat:18.11,lng:-77.30},"Japan":{lat:36.20,lng:138.25},"Jordan":{lat:30.59,lng:36.24},"Kazakhstan":{lat:48.02,lng:66.92},"Kenya":{lat:-0.02,lng:37.91},"Kuwait":{lat:29.31,lng:47.48},"Latvia":{lat:56.88,lng:24.60},"Lebanon":{lat:33.85,lng:35.86},"Libya":{lat:26.34,lng:17.23},"Lithuania":{lat:55.17,lng:23.88},"Luxembourg":{lat:49.82,lng:6.13},"Malaysia":{lat:4.21,lng:101.98},"Mexico":{lat:23.63,lng:-102.55},"Mongolia":{lat:46.86,lng:103.85},"Morocco":{lat:31.79,lng:-7.09},"Mozambique":{lat:-18.67,lng:35.53},"Myanmar":{lat:21.91,lng:95.96},"Nepal":{lat:28.39,lng:84.12},"Netherlands":{lat:52.13,lng:5.29},"New Zealand":{lat:-40.90,lng:174.89},"Nicaragua":{lat:12.87,lng:-85.21},"Nigeria":{lat:9.08,lng:8.68},"North Korea":{lat:40.34,lng:127.51},"Norway":{lat:60.47,lng:8.47},"Oman":{lat:21.47,lng:55.98},"Pakistan":{lat:30.38,lng:69.35},"Panama":{lat:8.54,lng:-80.78},"Paraguay":{lat:-23.44,lng:-58.44},"Peru":{lat:-9.19,lng:-75.02},"Philippines":{lat:12.88,lng:121.77},"Poland":{lat:51.92,lng:19.15},"Portugal":{lat:39.40,lng:-8.22},"Qatar":{lat:25.35,lng:51.18},"Romania":{lat:45.94,lng:24.97},"Russia":{lat:61.52,lng:105.32},"Saudi Arabia":{lat:23.89,lng:45.08},"Serbia":{lat:44.02,lng:21.01},"Singapore":{lat:1.35,lng:103.82},"Slovakia":{lat:48.67,lng:19.70},"Slovenia":{lat:46.15,lng:14.99},"South Africa":{lat:-30.56,lng:22.94},"South Korea":{lat:35.91,lng:127.77},"Spain":{lat:40.46,lng:-3.75},"Sri Lanka":{lat:7.87,lng:80.77},"Sudan":{lat:12.86,lng:30.22},"Sweden":{lat:60.13,lng:18.64},"Switzerland":{lat:46.82,lng:8.23},"Taiwan":{lat:23.70,lng:120.96},"Tanzania":{lat:-6.37,lng:34.89},"Thailand":{lat:15.87,lng:100.99},"Tunisia":{lat:33.89,lng:9.54},"Turkey":{lat:38.96,lng:35.24},"Ukraine":{lat:48.38,lng:31.17},"United Arab Emirates":{lat:23.42,lng:53.85},"United Kingdom":{lat:55.38,lng:-3.44},"United States":{lat:37.09,lng:-95.71},"Uruguay":{lat:-32.52,lng:-55.77},"Uzbekistan":{lat:41.38,lng:64.59},"Venezuela":{lat:6.42,lng:-66.59},"Vietnam":{lat:14.06,lng:108.28},
};

// Zoom levels per country size
const COUNTRY_ZOOM = {
  "Russia":3,"Canada":3,"United States":4,"China":4,"Brazil":4,"Australia":4,"India":5,"Argentina":4,"Kazakhstan":4,"Algeria":5,"Indonesia":4,"Mexico":5,"Saudi Arabia":5,"Mongolia":5,
};

import MapControls from "../components/map/MapControls";
import { resolveMediaUrl } from "../lib/apiConfig";

const isValidLatLng = (position) => (
  position &&
  Number.isFinite(position.lat) &&
  Number.isFinite(position.lng) &&
  Math.abs(position.lat) <= 90 &&
  Math.abs(position.lng) <= 180
);

const SafeAdvancedMarker = ({ position, children, onClick, title, zIndex }) => {
  const map = useMap();
  const markerLibrary = useMapsLibrary("marker");
  const markerRef = useRef(null);
  const clickListenerRef = useRef(null);
  const [contentElement, setContentElement] = useState(null);

  useEffect(() => {
    if (!map || !markerLibrary || !isValidLatLng(position)) return;

    const content = document.createElement("div");
    content.style.pointerEvents = "auto";

    const marker = new markerLibrary.AdvancedMarkerElement({
      position,
      content,
      title: title || "",
      zIndex,
    });

    marker.map = map;
    markerRef.current = marker;
    setContentElement(content);

    return () => {
      if (clickListenerRef.current) {
        window.google?.maps?.event?.removeListener(clickListenerRef.current);
        clickListenerRef.current = null;
      }
      marker.map = null;
      content.remove();
      markerRef.current = null;
      setContentElement(null);
    };
  }, [map, markerLibrary, position?.lat, position?.lng]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const marker = markerRef.current;
    if (!marker || !isValidLatLng(position)) return;
    marker.position = position;
    marker.title = title || "";
    if (zIndex !== undefined) marker.zIndex = zIndex;
  }, [position, title, zIndex]);

  useEffect(() => {
    const marker = markerRef.current;
    if (!marker) return;

    if (clickListenerRef.current) {
      window.google?.maps?.event?.removeListener(clickListenerRef.current);
      clickListenerRef.current = null;
    }

    if (onClick) {
      marker.gmpClickable = true;
      clickListenerRef.current = marker.addListener("click", onClick);
    }
  }, [onClick]);

  if (!contentElement) return null;
  return createPortal(children, contentElement);
};

// Map Content Component (inside Map context)
const MapContent = ({ 
  selectedLocation, 
  setSelectedLocation, 
  userLocation,
  onMapClick,
  shouldCenterOnUser,
  onCenteredOnUser,
  locationStatus,
  markers,
  onMarkerClick,
  onViewportChange,
  panTarget,
  onPanComplete,
  onRequestLocation,
  fitToMarkers,
  fitBoundsCoords,
}) => {
  const map = useMap();
  const returnTargetRef = useRef(panTarget); // capture initial URL return target
  const debounceRef = useRef(null);

  // Center map on user location when it becomes available (auto-centering on load)
  useEffect(() => {
    if (map && userLocation && shouldCenterOnUser) {
      map.panTo(userLocation);
      map.setZoom(13);
      if (onCenteredOnUser) {
        onCenteredOnUser();
      }
    }
  }, [map, userLocation, shouldCenterOnUser, onCenteredOnUser]);

  // Initial URL return zoom (from dashboard/audio player) — enforced repeatedly
  useEffect(() => {
    if (!map || !returnTargetRef.current) return;
    const target = returnTargetRef.current;
    returnTargetRef.current = null;

    const targetZoom = target.zoom || 19;
    const targetCenter = { lat: target.lat, lng: target.lng };

    // Set immediately
    map.setCenter(targetCenter);
    map.setZoom(targetZoom);

    // Keep enforcing for 3s to combat any async overrides (library, tiles, clusterer)
    let attempts = 0;
    const interval = setInterval(() => {
      attempts++;
      if (map.getZoom() !== targetZoom) {
        map.setCenter(targetCenter);
        map.setZoom(targetZoom);
      }
      if (attempts >= 6) clearInterval(interval);
    }, 500);

    if (onPanComplete) onPanComplete();
    return () => clearInterval(interval);
  }, [map]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pan to search results (interactive, after initial URL zoom is done)
  useEffect(() => {
    if (!map || !panTarget || returnTargetRef.current) return;
    map.panTo({ lat: panTarget.lat, lng: panTarget.lng });
    map.setZoom(panTarget.zoom || 19);
    if (onPanComplete) onPanComplete();
  }, [map, panTarget, onPanComplete]);

  // Fit map bounds to show all purchased places when My Places filter activates
  useEffect(() => {
    if (!map || !fitToMarkers || !fitBoundsCoords || !fitBoundsCoords.length) return;
    const bounds = new window.google.maps.LatLngBounds();
    fitBoundsCoords.forEach(p => bounds.extend({ lat: p.latitude, lng: p.longitude }));
    const isMobile = window.innerWidth < 1024;
    const padding = isMobile
      ? { top: 100, bottom: 80, left: 40, right: 40 }
      : { top: 80, bottom: 40, left: 280, right: 40 };
    // Small delay to ensure map is fully ready
    setTimeout(() => map.fitBounds(bounds, padding), 300);
  }, [map, fitToMarkers, fitBoundsCoords]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle map idle (after pan/zoom) — debounced viewport change
  useEffect(() => {
    if (!map) return;
    const listener = map.addListener("idle", () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const bounds = map.getBounds();
        const zoom = map.getZoom();
        if (bounds && onViewportChange) {
          const ne = bounds.getNorthEast();
          const sw = bounds.getSouthWest();
          onViewportChange({
            ne_lat: ne.lat(), ne_lng: ne.lng(),
            sw_lat: sw.lat(), sw_lng: sw.lng(),
            zoom: zoom || 14,
          });
        }
      }, 600);
    });
    return () => {
      if (listener) window.google.maps.event.removeListener(listener);
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [map, onViewportChange]);

  const handleZoomIn = useCallback(() => {
    if (map) {
      map.setZoom((map.getZoom() || 14) + 1);
    }
  }, [map]);

  const handleZoomOut = useCallback(() => {
    if (map) {
      map.setZoom((map.getZoom() || 14) - 1);
    }
  }, [map]);

  const handleCenter = useCallback(() => {
    // A return-to-map coordinate represents the last guide, not the user's
    // position. Always ask geolocation for a fresh fix on an explicit tap.
    if (onRequestLocation) onRequestLocation(true);
  }, [onRequestLocation]);

  return (
    <>
      <MapControls 
        onCenter={handleCenter}
        onZoomIn={handleZoomIn}
        onZoomOut={handleZoomOut}
        locationStatus={locationStatus}
      />
      
      {/* User Location Marker - Larger for mobile visibility */}
      {userLocation && (
        <SafeAdvancedMarker position={userLocation}>
          <div className="relative">
            <div className="w-8 h-8 bg-blue-500 rounded-full border-4 border-white shadow-lg animate-pulse" />
            <div className="absolute inset-0 w-8 h-8 bg-blue-500/30 rounded-full animate-ping" />
          </div>
        </SafeAdvancedMarker>
      )}
      
      {/* Individual markers */}
      {markers.map((marker) => {
        const config = CATEGORY_CONFIG[marker.category] || CATEGORY_CONFIG.landmark;
        const IconComponent = config.icon;
        const isSelected = selectedLocation?.id === marker.id;

        return (
          <SafeAdvancedMarker
            key={marker.id}
            position={{ lat: marker.latitude, lng: marker.longitude }}
            onClick={() => onMarkerClick(marker)}
            title={marker.name}
          >
            <div
              className={`flex flex-col items-center cursor-pointer transition-transform ${isSelected ? 'scale-125' : 'hover:scale-110'}`}
            >
              <div className={`w-12 h-12 ${config.color} rounded-full flex items-center justify-center shadow-lg border-3 border-white ${isSelected ? 'ring-4 ring-primary/50' : ''}`}>
                <IconComponent className="w-6 h-6 text-white" />
              </div>
              <div className={`w-3 h-3 ${config.color} rotate-45 -mt-2`} />
              <div className={`absolute -bottom-8 left-1/2 -translate-x-1/2 whitespace-nowrap bg-card px-2 py-1 rounded shadow-md text-xs font-medium ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'} transition-opacity`}>
                {marker.name}
              </div>
            </div>
          </SafeAdvancedMarker>
        );
      })}
    </>
  );
};

// Audio Player Component
// Main Map Page Component
const MapPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, updateCredits, loading: authLoading } = useAuth();

  // Check if returning from audio player with coordinates
  const returnLat = searchParams.get("lat");
  const returnLng = searchParams.get("lng");
  const returnZoom = searchParams.get("zoom");
  const returnMarker = searchParams.get("marker");
  const initialMyPlaces = searchParams.get("myplaces") === "1";
  const returnLocation = returnLat && returnLng ? { lat: parseFloat(returnLat), lng: parseFloat(returnLng) } : null;

  const [userLocation, setUserLocation] = useState(returnLocation);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [selectedMarker, setSelectedMarker] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [panTarget, setPanTarget] = useState(
    returnLocation ? { lat: returnLocation.lat, lng: returnLocation.lng, zoom: parseInt(returnZoom) || 17 } : null
  );
  const searchRef = useRef(null);
  const debounceSearchRef = useRef(null);
  const autoSelectedReturnMarkerRef = useRef(false);
  const quickBuyDialogRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(null); // {progress, step}
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [descExpanded, setDescExpanded] = useState(false);
  const [locationStatus, setLocationStatus] = useState(returnLocation ? "granted" : "detecting");
  const [shouldCenterOnUser, setShouldCenterOnUser] = useState(!returnLocation && !initialMyPlaces);
  const [markers, setMarkers] = useState([]);
  const [markersLoading, setMarkersLoading] = useState(true);
  const [activeCategories, setActiveCategories] = useState(new Set(ALL_FILTER_CATEGORIES));
  const [myPlacesFilter, setMyPlacesFilter] = useState(initialMyPlaces);
  const [fitToMarkersTrigger, setFitToMarkersTrigger] = useState(initialMyPlaces ? 1 : 0);
  const [purchasedPlaceIds, setPurchasedPlaceIds] = useState(new Set());
  const [purchasedLanguagesByPlace, setPurchasedLanguagesByPlace] = useState({});
  const [ownershipCheckingPlaceId, setOwnershipCheckingPlaceId] = useState(null);
  const [purchasedPlaceCoords, setPurchasedPlaceCoords] = useState([]);
  const [quickBuyOpen, setQuickBuyOpen] = useState(false);
  const [purchaseLanguage, setPurchaseLanguage] = useState(user?.default_audio_language || "en");
  const [supportedLanguages, setSupportedLanguages] = useState(FALLBACK_AUDIO_LANGUAGES);
  const [showLocationPrompt, setShowLocationPrompt] = useState(false);
  const lastViewportRef = useRef(null);

  useEffect(() => {
    if (!quickBuyOpen) return undefined;

    setPurchaseLanguage(user?.default_audio_language || "en");
    let cancelled = false;
    contentApi.getLanguages()
      .then((response) => {
        const languages = response.data?.languages;
        if (!cancelled && Array.isArray(languages) && languages.length > 0) {
          setSupportedLanguages(languages);
        }
      })
      .catch((error) => {
        captureError(error, { action: "load_purchase_languages" });
      });

    return () => {
      cancelled = true;
    };
  }, [quickBuyOpen, user?.default_audio_language]);

  const loadPurchasedPlaces = useCallback(async () => {
    if (!user?.id) {
      setPurchasedPlaceIds(new Set());
      setPurchasedLanguagesByPlace({});
      setPurchasedPlaceCoords([]);
      return;
    }
    try {
      const res = await api.get("/user/purchased-places");
      const placeIds = res.data.place_ids || [];
      setPurchasedPlaceIds(new Set(placeIds));
      setPurchasedLanguagesByPlace(res.data.languages_by_place || {});
      const coords = (res.data.places || []).filter(p => p.latitude && p.longitude);
      setPurchasedPlaceCoords(coords);

      // Load full marker data for purchased places so they always appear on the map
      if (placeIds.length > 0) {
        try {
          const markersRes = await api.post("/content/markers-by-ids", { ids: placeIds });
          const items = markersRes.data.items || markersRes.data || [];
          if (items.length > 0) {
            setMarkers(prev => {
              const existing = new Map(prev.map(m => [m.id, m]));
              items.forEach(m => existing.set(m.id, m));
              return Array.from(existing.values());
            });
          }
        } catch (error) {
          captureError(error, { action: "load_purchased_place_markers", count: placeIds.length });
        }
      }

      // If My Places filter is already active (e.g. from URL param), trigger fitBounds now
      if (initialMyPlaces && coords.length > 0) {
        setFitToMarkersTrigger(t => t + 1);
      }
    } catch (error) {
      captureError(error, { action: "load_purchased_places" });
    }
  }, [initialMyPlaces, user?.id]);

  // Fetch purchased place IDs for "My Places" and refresh when returning to
  // the map after a purchase/player visit.
  useEffect(() => {
    loadPurchasedPlaces();
  }, [loadPurchasedPlaces]);

  const recordPurchasedLanguage = useCallback((placeId, language) => {
    if (!placeId) return;
    const normalizedLanguage = (language || "en").toLowerCase();
    setPurchasedPlaceIds(previous => new Set([...previous, placeId]));
    setPurchasedLanguagesByPlace(previous => ({
      ...previous,
      [placeId]: Array.from(new Set([
        ...(previous[placeId] || []),
        normalizedLanguage,
      ])),
    }));
  }, []);

  const refreshMarkerOwnership = useCallback(async (marker) => {
    if (!user?.id || !marker?.id) return [];
    setOwnershipCheckingPlaceId(marker.id);
    try {
      const response = await userApi.getPurchasedLanguages(marker.id);
      const languages = (response.data?.languages || [])
        .map(language => String(language).toLowerCase())
        .filter(Boolean);
      if (languages.length) {
        setPurchasedPlaceIds(previous => new Set([...previous, marker.id]));
      }
      setPurchasedLanguagesByPlace(previous => ({...previous, [marker.id]: languages}));
      return languages;
    } catch (error) {
      captureError(error, {action: "load_marker_ownership", placeId: marker.id});
      return purchasedLanguagesByPlace[marker.id] || [];
    } finally {
      setOwnershipCheckingPlaceId(current => current === marker.id ? null : current);
    }
  }, [purchasedLanguagesByPlace, user?.id]);

  // Sync myPlacesFilter from URL when navigating to this page
  useEffect(() => {
    if (initialMyPlaces) {
      setMyPlacesFilter(true);
      setFitToMarkersTrigger(t => t + 1);
    }
  }, [initialMyPlaces]);

  // Toggle a category filter
  const toggleCategory = useCallback((cat) => {
    setActiveCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) {
        next.delete(cat);
      } else {
        next.add(cat);
      }
      return next;
    });
  }, []);

  const toggleAllCategories = useCallback(() => {
    setActiveCategories(prev => 
      prev.size === ALL_FILTER_CATEGORIES.length ? new Set() : new Set(ALL_FILTER_CATEGORIES)
    );
  }, []);

  // Filtered markers based on active categories and My Places filter
  const filteredMarkers = markers.filter(m => {
    const cat = m.category || "landmark";
    if (!activeCategories.has(cat)) return false;
    if (myPlacesFilter && !purchasedPlaceIds.has(m.id)) return false;
    return true;
  });

  // Viewport-based place discovery — called when map idle
  const handleViewportChange = useCallback(async (viewport) => {
    // Skip if viewport hasn't changed significantly
    const last = lastViewportRef.current;
    if (last && Math.abs(last.ne_lat - viewport.ne_lat) < 0.001 && Math.abs(last.ne_lng - viewport.ne_lng) < 0.001) {
      return;
    }
    lastViewportRef.current = viewport;

    try {
      const res = await api.get("/content/discover", { params: viewport });
      const items = res.data.items || [];
      setMarkers(prev => {
        // Merge new items with existing, dedup by id
        const existing = new Map(prev.map(m => [m.id, m]));
        items.forEach(m => existing.set(m.id, m));
        return Array.from(existing.values());
      });
    } catch (err) {
      captureError(err, { action: "discover_places", viewport });
    }
  }, []);

  // Initial content fetch as fallback (when location resolves)
  useEffect(() => {
    if (locationStatus === "detecting") return;
    
    const fetchContent = async () => {
      try {
        setMarkersLoading(true);
        let response;
        if (userLocation) {
          response = await contentApi.getNearby(userLocation.lat, userLocation.lng, { radius: 50, limit: 100 });
        } else {
          response = await contentApi.list({ limit: 100 });
        }
        setMarkers(response.data.items || []);
      } catch (error) {
        captureError(error, { action: "fetch_initial_content" });
      } finally {
        setMarkersLoading(false);
      }
    };
    
    fetchContent();
  }, [locationStatus, userLocation]);

  // Auto-select marker from URL param (e.g. from "Explore Nearby")
  useEffect(() => {
    if (!returnMarker || !markers.length || autoSelectedReturnMarkerRef.current) return;
    const target = markers.find(m => m.id === returnMarker);
    if (target) {
      autoSelectedReturnMarkerRef.current = true;
      setSelectedMarker(target);
      setDetailsOpen(true);
    }
  }, [returnMarker, markers]); // eslint-disable-line react-hooks/exhaustive-deps

  // If returnMarker isn't in markers yet, fetch it explicitly
  useEffect(() => {
    if (!returnMarker) return;
    const exists = markers.find(m => m.id === returnMarker);
    if (!exists) {
      api.post("/content/markers-by-ids", { ids: [returnMarker] })
        .then(res => {
          const items = res.data.items || res.data || [];
          if (items.length > 0) {
            setMarkers(prev => {
              const existing = new Map(prev.map(m => [m.id, m]));
              items.forEach(m => existing.set(m.id, m));
              return Array.from(existing.values());
            });
          }
        })
        .catch((error) => {
          captureError(error, { action: "load_return_marker", markerId: returnMarker });
        });
    }
  }, [returnMarker]); // eslint-disable-line react-hooks/exhaustive-deps



  // Request user location
  const requestLocation = useCallback((showToast = false) => {
    // Default to user's country center, or New York
    const countryCenter = user?.country && COUNTRY_CENTERS[user.country];
    const defaultLocation = countryCenter || { lat: 40.7128, lng: -74.006 };
    
    if (!navigator.geolocation) {
      setLocationStatus("unavailable");
      setUserLocation(defaultLocation);
      if (showToast) toast.info("Geolocation not supported by your browser.");
      return;
    }

    setLocationStatus("detecting");
    if (showToast) toast.info("Detecting your location...");

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          lat: position.coords.latitude,
          lng: position.coords.longitude
        };
        setUserLocation(location);
        setLocationStatus("granted");
        if (showToast) {
          // User clicked location button — center immediately via panTarget
          setPanTarget({ lat: location.lat, lng: location.lng, zoom: 15 });
        } else {
          // Auto-detect on mount — use shouldCenterOnUser flow
          setShouldCenterOnUser(true);
        }
      },
      (error) => {
        addBreadcrumb("Geolocation request failed", "browser.geolocation", {
          code: error.code,
          message: error.message,
          showToast,
        }, "warning");
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            setLocationStatus("denied");
            if (showToast) {
              toast.info("Location access denied. Enable location in your browser settings to center the map on your position.");
            }
            break;
          case error.POSITION_UNAVAILABLE:
            setLocationStatus("unavailable");
            if (showToast) toast.info("Location unavailable.");
            break;
          case error.TIMEOUT:
            setLocationStatus("unavailable");
            if (showToast) toast.info("Location request timed out.");
            break;
          default:
            captureMessage("Unexpected geolocation failure", "warning", {
              code: error.code,
              message: error.message,
            });
            setLocationStatus("unavailable");
            if (showToast) toast.info("Could not get location.");
        }
        
        if (!userLocation) {
          setUserLocation(defaultLocation);
        }
      },
      { enableHighAccuracy: false, timeout: 5000, maximumAge: 60000 }
    );
  }, [userLocation, user]);

  // Get user location on mount (skip if returning from player)
  useEffect(() => {
    if (!returnLocation) {
      // Check if this is first visit after registration
      if (localStorage.getItem("needs_location_onboarding") === "true") {
        setShowLocationPrompt(true);
      } else {
        requestLocation();
      }
    } else {
      // Returning from dashboard/player with coordinates — skip geolocation
      setLocationStatus("granted");
    }
    // Lock body scroll for map page
    document.body.classList.add("map-page-active");
    return () => document.body.classList.remove("map-page-active");
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- mount-only; location changes must not restart permission flow

  const handleCenteredOnUser = useCallback(() => {
    setShouldCenterOnUser(false);
  }, []);

  const handleMapClick = useCallback(async (e) => {
    if (!e.detail?.latLng) return;
    // Close any open details when clicking the map background
    setSelectedLocation(null);
    setSelectedMarker(null);
    setDetailsOpen(false);
  }, []);

  const handleMarkerClick = useCallback((marker) => {
    setSelectedLocation({
      id: marker.id,
      lat: marker.latitude,
      lng: marker.longitude,
      name: marker.name
    });
    setSelectedMarker(marker);
    setDetailsOpen(true);
    setDescExpanded(false);
    refreshMarkerOwnership(marker);
  }, [refreshMarkerOwnership]);

  // Fetch address/image for markers missing them
  useEffect(() => {
    if (!selectedMarker || !selectedMarker.google_place_id) return;
    if (selectedMarker.address && selectedMarker.image_url) return;
    let cancelled = false;
    contentApi.getPlaceDetails(selectedMarker.google_place_id)
      .then(res => {
        if (cancelled) return;
        const d = res.data || {};
        const updated = {
          ...selectedMarker,
          address: d.address || selectedMarker.address || "",
          description: d.description || selectedMarker.description,
          image_url: d.image_url || selectedMarker.image_url || "",
        };
        setSelectedMarker(updated);
        setMarkers(prev => prev.map(m => m.id === selectedMarker.id ? updated : m));
      })
      .catch((error) => {
        captureError(error, { action: "load_marker_place_details", markerId: selectedMarker.id, placeId: selectedMarker.google_place_id });
      });
    return () => { cancelled = true; };
  }, [selectedMarker?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    if (debounceSearchRef.current) clearTimeout(debounceSearchRef.current);

    if (value.length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    debounceSearchRef.current = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const res = await contentApi.searchAutocomplete(
          value,
          userLocation?.lat,
          userLocation?.lng
        );
        setSuggestions(res.data.suggestions || []);
        setShowSuggestions(true);
      } catch {
        setSuggestions([]);
      } finally {
        setSearchLoading(false);
      }
    }, 300);
  }, [userLocation]);

  const handleSelectSuggestion = useCallback(async (suggestion) => {
    setShowSuggestions(false);
    setSearchQuery(suggestion.name);
    setSuggestions([]);

    // Check if this place already exists in local markers
    const localMatch = markers.find(m =>
      m.google_place_id === suggestion.place_id
    );
    if (localMatch) {
      handleMarkerClick(localMatch);
      setPanTarget({ lat: localMatch.latitude, lng: localMatch.longitude });
      return;
    }

    // Anonymous browsing stays read-only: fetch Google details without creating DB markers.
    if (!user) {
      try {
        setSearchLoading(true);
        const res = await contentApi.getPlaceDetails(suggestion.place_id);
        const place = res.data;
        const marker = {
          id: `google:${suggestion.place_id}`,
          google_place_id: suggestion.place_id,
          name: place.name || suggestion.name,
          description: place.description || "",
          address: place.address || "",
          latitude: place.latitude,
          longitude: place.longitude,
          rating: place.rating || 0,
          review_count: place.review_count || 0,
          image_url: place.image_url || "",
          category: "landmark",
          has_audio: false,
        };
        if (marker.latitude && marker.longitude) {
          setPanTarget({ lat: marker.latitude, lng: marker.longitude });
          setSelectedLocation({ id: marker.id, lat: marker.latitude, lng: marker.longitude, name: marker.name });
          setSelectedMarker(marker);
          setDetailsOpen(true);
        }
      } catch {
        toast.error("Could not find that place");
      } finally {
        setSearchLoading(false);
      }
      return;
    }

    // Ensure marker exists in DB (creates from Google Places if needed)
    try {
      setSearchLoading(true);
      const res = await contentApi.ensureMarkerFromPlace(suggestion.place_id);
      const marker = res.data;
      if (marker.latitude && marker.longitude) {
        setPanTarget({ lat: marker.latitude, lng: marker.longitude });
        setSelectedLocation({
          id: marker.id,
          lat: marker.latitude,
          lng: marker.longitude,
          name: marker.name,
        });
        setSelectedMarker(marker);
        setMarkers(prev => {
          const exists = prev.some(m => m.id === marker.id);
          return exists ? prev : [...prev, marker];
        });
        setDetailsOpen(true);
      }
    } catch (err) {
      // Log the real error so we can diagnose Safari-specific failures.
      console.error("ensureMarkerFromPlace failed:", err?.response?.status, err?.response?.data, err?.message);
      const detail = err?.response?.data?.detail;
      toast.error(detail || "Could not find that place. Please try again.");
    } finally {
      setSearchLoading(false);
    }
  }, [markers, handleMarkerClick, user]);

  // Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleClickOutside);
    };
  }, []);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!searchQuery.trim()) return;
    // If suggestions are showing, select the first one
    if (suggestions.length > 0) {
      handleSelectSuggestion(suggestions[0]);
      return;
    }
    // Otherwise search in local markers
    const localMatch = markers.find(m => 
      m.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
    if (localMatch) {
      handleMarkerClick(localMatch);
      setPanTarget({ lat: localMatch.latitude, lng: localMatch.longitude });
      return;
    }
  };

  const handleGenerateSummary = async () => {
    if (!selectedMarker) return;
    
    // Auth wall: require login to access audio guides
    if (!user) {
      toast.info("Sign in to access audio guides");
      navigate("/login?redirect=/explore");
      return;
    }
    
    const lang = (user?.default_audio_language || "en").toLowerCase();
    let ownedLanguages = purchasedLanguagesByPlace[selectedMarker.id] || [];
    if (!ownedLanguages.length) {
      ownedLanguages = await refreshMarkerOwnership(selectedMarker);
    }
    if (ownedLanguages.length) {
      const playbackLanguage = ownedLanguages.includes(lang) ? lang : ownedLanguages[0];
      navigate(`/player/${selectedMarker.id}?lang=${playbackLanguage}`);
      return;
    }
    
    // Show quick-buy dialog
    setPurchaseLanguage(lang);
    setQuickBuyOpen(true);
  };

  // Use 1 credit and generate the audio with progress polling
  const handleUseCredit = async () => {
    setQuickBuyOpen(false);
    setLoading(true);
    setGenerationProgress({ progress: 0, step: "Starting..." });
    const lang = (purchaseLanguage || user?.default_audio_language || "en").toLowerCase();

    const ownedLanguages = purchasedLanguagesByPlace[selectedMarker.id] || [];
    if (ownedLanguages.length) {
      setLoading(false);
      setGenerationProgress(null);
      const playbackLanguage = ownedLanguages.includes(lang) ? lang : ownedLanguages[0];
      navigate(`/player/${selectedMarker.id}?lang=${playbackLanguage}`);
      return;
    }

    addBreadcrumb("Audio guide generation requested", "content.generation", {
      placeId: selectedMarker.id,
      lang,
      source: "use_credit",
    });

    try {
      const response = await contentApi.generateSummary(selectedMarker.id, lang, true);
      const data = response.data;

      // If cached, go directly to player
      if (data.status === "complete" && data.marker) {
        addBreadcrumb("Audio guide returned from cache", "content.generation", {
          placeId: data.marker.id,
          lang,
        });
        setSelectedMarker(data.marker);
        setMarkers(prev => prev.map(m => m.id === data.marker.id ? data.marker : m));
        recordPurchasedLanguage(data.marker.id, lang);
        setGenerationProgress(null);
        setLoading(false);
        toast.success("Audio guide ready!");
        navigate(`/player/${data.marker.id}?lang=${lang}`);
        return;
      }

      // Navigate to player immediately with job_id for chapter-by-chapter streaming
      if (data.job_id) {
        addBreadcrumb("Audio guide generation job started", "content.generation", {
          placeId: selectedMarker.id,
          lang,
          jobId: data.job_id,
        });
        setGenerationProgress(null);
        setLoading(false);
        recordPurchasedLanguage(selectedMarker.id, lang);
        navigate(`/player/${selectedMarker.id}?lang=${lang}&job_id=${data.job_id}`);
        return;
      }

      // No job_id returned — unexpected response
      captureMessage("Generate summary returned unexpected response", "warning", {
        placeId: selectedMarker.id,
        lang,
        responseKeys: Object.keys(data || {}),
      });
      setGenerationProgress(null);
      setLoading(false);
      toast.error("Something went wrong. Please try again.");
    } catch (error) {
      if (error.response?.status !== 402) {
        captureError(error, { action: "generate_summary_use_credit", placeId: selectedMarker.id, lang });
      }
      setGenerationProgress(null);
      setLoading(false);
      if (error.response?.status === 402) {
        toast.error("No credits available.");
        setQuickBuyOpen(true);
      } else {
        toast.error("Failed to generate summary. Please try again.");
      }
    }
  };

  const handlePlayAudio = async () => {
    if (!selectedMarker) return;
    
    // Auth wall
    if (!user) {
      toast.info("Sign in to listen to audio guides");
      navigate("/login?redirect=/explore");
      return;
    }
    
    const preferredLanguage = (user?.default_audio_language || "en").toLowerCase();
    let ownedLanguages = purchasedLanguagesByPlace[selectedMarker.id] || [];
    if (!ownedLanguages.length) {
      setAudioLoading(true);
      ownedLanguages = await refreshMarkerOwnership(selectedMarker);
      setAudioLoading(false);
    }

    // The map's Play action must never start a second purchase/generation. The
    // player resolves cached or in-progress chapters for the owned language.
    const playbackLanguage = ownedLanguages.includes(preferredLanguage)
      ? preferredLanguage
      : ownedLanguages[0] || preferredLanguage;
    navigate(`/player/${selectedMarker.id}?lang=${playbackLanguage}`);
  };

  const selectedOwnedLanguages = selectedMarker
    ? purchasedLanguagesByPlace[selectedMarker.id] || []
    : [];
  const selectedMarkerOwned = Boolean(
    user && selectedMarker && (
      selectedOwnedLanguages.length > 0 ||
      purchasedPlaceIds.has(selectedMarker.id) ||
      selectedMarker.owned ||
      selectedMarker.is_owned ||
      selectedMarker.purchased ||
      selectedMarker.is_purchased
    )
  );

  // Only show error if API key is completely missing
  if (!GOOGLE_MAPS_API_KEY) {
    if (!reportedMissingGoogleMapsKey) {
      reportedMissingGoogleMapsKey = true;
      captureMessage("Google Maps browser API key is missing", "warning", {
        page: "MapPage",
      });
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Map Not Available</h2>
            <p className="text-muted-foreground">
              Google Maps API key is not configured. Please add REACT_APP_GOOGLE_MAPS_API_KEY to your environment.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!GOOGLE_MAPS_MAP_ID) {
    if (!reportedMissingGoogleMapsMapId) {
      reportedMissingGoogleMapsMapId = true;
      captureMessage("Google Maps Map ID is missing", "warning", {
        page: "MapPage",
        env_var: "REACT_APP_GOOGLE_MAPS_MAP_ID",
      });
    }
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <Card className="max-w-md">
          <CardContent className="p-6 text-center">
            <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Map Not Available</h2>
            <p className="text-muted-foreground">
              Google Maps Map ID is not configured. Please add REACT_APP_GOOGLE_MAPS_MAP_ID to your environment.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mobile-container flex flex-col bg-background">
      {/* Top Bar - Mobile optimized with safe area */}
      <header className="absolute top-0 left-0 right-0 z-40 p-3 pt-[max(12px,env(safe-area-inset-top))] lg:left-64 pointer-events-none">
        <div className="flex items-center gap-2 lg:gap-3 lg:max-w-2xl lg:mx-auto pointer-events-auto">
          {/* Logo Button (Mobile) - Opens sidebar */}
          <button
            className="lg:hidden w-14 h-14 rounded-2xl shadow-lg bg-white border border-gray-200 flex-shrink-0 active:scale-95 transition-transform overflow-hidden p-1.5"
            onClick={() => setSidebarOpen(true)}
            data-testid="menu-btn"
          >
            <img src="/icon-192.png" alt="Culture Ken" className="w-full h-full object-contain rounded-xl" />
          </button>
          
          {/* Search Bar with Autocomplete */}
          <form onSubmit={handleSearch} className="flex-1" ref={searchRef}>
            <div className="relative">
              <Input
                type="text"
                placeholder="Search for a place..."
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
                className="h-14 pl-14 pr-10 rounded-2xl shadow-lg bg-card border-0 text-base touch-target-lg"
                autoComplete="off"
                data-testid="search-input"
              />
              <Search className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              {searchLoading && (
                <Loader2 className="absolute right-4 top-1/2 -translate-y-1/2 w-4 h-4 animate-spin text-muted-foreground" />
              )}

              {/* Autocomplete Dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div
                  className="absolute top-full left-0 right-0 mt-2 bg-card rounded-2xl shadow-xl border border-border overflow-hidden z-50"
                  data-testid="search-suggestions"
                >
                  {suggestions.map((s, i) => (
                    <button
                      key={s.place_id || i}
                      type="button"
                      className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/50 transition-colors border-b border-border last:border-0"
                      onClick={() => handleSelectSuggestion(s)}
                      data-testid={`search-suggestion-${i}`}
                    >
                      <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="font-medium text-sm text-foreground truncate">{s.name}</p>
                        <p className="text-xs text-muted-foreground truncate">{s.description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </form>
          
          {/* Credits Badge / Sign In - Hidden on small mobile */}
          {user ? (
            <Button
              variant="secondary"
              className="hidden sm:flex h-14 px-5 rounded-2xl shadow-lg bg-card gap-2 active:scale-95 transition-transform"
              onClick={() => navigate("/pricing")}
              data-testid="credits-btn"
            >
              <CreditCard className="w-5 h-5 text-primary" />
              <span className="font-semibold text-lg">{user.credits || 0}</span>
            </Button>
          ) : (
            <Button
              variant="secondary"
              className="hidden sm:flex h-14 px-5 rounded-2xl shadow-lg bg-card gap-2 active:scale-95 transition-transform"
              onClick={() => navigate("/login?redirect=/explore")}
              data-testid="signin-header-btn"
            >
              <LogIn className="w-5 h-5 text-primary" />
              <span className="font-semibold">Sign In</span>
            </Button>
          )}
        </div>
      </header>

      {/* Generating Content Banner (floating overlay — below filters) */}
      {loading && generationProgress && (
        <div className="absolute top-[calc(max(12px,env(safe-area-inset-top))+108px)] lg:top-[calc(max(12px,env(safe-area-inset-top))+68px)] left-1/2 -translate-x-1/2 z-40" data-testid="generation-banner">
          <div className="relative overflow-hidden bg-card/95 backdrop-blur-md shadow-lg rounded-full px-5 py-2.5 flex items-center gap-3 border border-primary/20">
            <div
              className="absolute inset-0 bg-gradient-to-r from-transparent via-primary/10 to-transparent"
              style={{ backgroundSize: '200% 100%', animation: 'shimmer 2s ease-in-out infinite' }}
            />
            <div className="relative flex items-center gap-3">
              <div className="w-2.5 h-2.5 rounded-full bg-primary animate-pulse" />
              <span className="text-sm font-medium text-foreground whitespace-nowrap">
                {generationProgress.step || "Generating content"}
              </span>
              <span className="text-xs text-muted-foreground tabular-nums">{generationProgress.progress}%</span>
            </div>
          </div>
        </div>
      )}

      {/* Category Filter Bar (Mobile + Tablet) */}
      <div className="absolute top-[calc(max(12px,env(safe-area-inset-top))+68px)] left-0 right-0 z-30 lg:hidden px-3">
        <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide" data-testid="mobile-category-filters">
          {user && (
            <button
              className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium shadow-md transition-all ${
                myPlacesFilter
                  ? "bg-rose-500 text-white"
                  : "bg-card text-muted-foreground"
              }`}
              onClick={() => {
                setMyPlacesFilter(prev => {
                  if (!prev) setFitToMarkersTrigger(t => t + 1);
                  return !prev;
                });
              }}
              data-testid="filter-my-places-btn"
            >
              <Heart className={`w-3.5 h-3.5 ${myPlacesFilter ? "fill-current" : ""}`} />
              My Places
            </button>
          )}
          <button
            className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium shadow-md transition-all ${
              activeCategories.size === ALL_FILTER_CATEGORIES.length
                ? "bg-primary text-primary-foreground"
                : "bg-card text-muted-foreground"
            }`}
            onClick={toggleAllCategories}
            data-testid="filter-all-btn"
          >
            <Filter className="w-3.5 h-3.5" />
            All
          </button>
          {Object.entries(CATEGORY_CONFIG).filter(([k]) => k !== "cultural").map(([key, cfg]) => {
            const Icon = cfg.icon;
            const active = activeCategories.has(key);
            return (
              <button
                key={key}
                className={`flex-shrink-0 flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-medium shadow-md transition-all ${
                  active ? `${cfg.color} text-white` : "bg-card text-muted-foreground opacity-60"
                }`}
                onClick={() => toggleCategory(key)}
                data-testid={`filter-${key}-btn`}
              >
                <Icon className="w-3.5 h-3.5" />
                {cfg.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Map */}
      <div className="flex-1 relative">
        {/* Location Status Indicator */}
        {locationStatus === "detecting" && (
          <div className="absolute top-20 left-1/2 -translate-x-1/2 z-20 bg-card rounded-full px-4 py-2 shadow-lg flex items-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin text-primary" />
            <span className="text-sm text-muted-foreground">Detecting your location...</span>
          </div>
        )}
        
        <APIProvider apiKey={GOOGLE_MAPS_API_KEY}>
          <GoogleMap
            defaultCenter={returnLocation || userLocation || (user?.home_city_lat && user?.home_city_lng ? { lat: user.home_city_lat, lng: user.home_city_lng } : null) || (user?.country && COUNTRY_CENTERS[user.country]) || { lat: 40.7128, lng: -74.006 }}
            defaultZoom={returnZoom ? parseInt(returnZoom) : (user?.home_city_lat && user?.home_city_lng ? 12 : (user?.country ? (COUNTRY_ZOOM[user.country] || 6) : 12))}
            mapId={GOOGLE_MAPS_MAP_ID}
            gestureHandling="greedy"
            disableDefaultUI={true}
            onClick={handleMapClick}
            className="w-full h-full"
          >
            <MapContent
              selectedLocation={selectedLocation}
              setSelectedLocation={setSelectedLocation}
              userLocation={userLocation}
              onMapClick={handleMapClick}
              shouldCenterOnUser={shouldCenterOnUser}
              onCenteredOnUser={handleCenteredOnUser}
              locationStatus={locationStatus}
              markers={filteredMarkers}
              onMarkerClick={handleMarkerClick}
              onViewportChange={handleViewportChange}
              panTarget={panTarget}
              onPanComplete={() => setPanTarget(null)}
              onRequestLocation={requestLocation}
              fitToMarkers={myPlacesFilter ? fitToMarkersTrigger : 0}
              fitBoundsCoords={purchasedPlaceCoords}
            />
          </GoogleMap>
        </APIProvider>
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex fixed left-0 top-0 bottom-0 w-64 bg-card border-r border-border z-30 flex-col">
        <div className="p-6 border-b border-border">
          <button
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => navigate("/")}
            data-testid="desktop-sidebar-home-link"
          >
            <img 
              src="/icon-192.png" 
              alt="Culture Ken"
              className="w-10 h-10 rounded-xl"
            />
            <span className="text-xl font-bold text-foreground">
              Culture Ken
            </span>
          </button>
        </div>
        
        <nav className="flex-1 p-4 space-y-2">
          <Button
            variant="ghost"
            className="w-full justify-start h-12 text-base bg-muted text-foreground font-semibold"
            onClick={() => navigate("/explore")}
            data-testid="nav-explore-btn"
          >
            <MapPin className="w-5 h-5 mr-3" />
            Explore
          </Button>
          <Button
            variant="ghost"
            className="w-full justify-start h-12 text-base"
            onClick={() => {
              sessionStorage.removeItem("cv_tour_last");
              navigate("/plan-tour");
            }}
            data-testid="nav-plan-tour-btn"
          >
            <Compass className="w-5 h-5 mr-3" />
            Plan a Trip
          </Button>
          {user && (
            <>
              <Button
                variant="ghost"
                className="w-full justify-start h-12 text-base"
                onClick={() => navigate("/dashboard")}
                data-testid="nav-dashboard-btn"
              >
                <Library className="w-5 h-5 mr-3" />
                Library
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start h-12 text-base"
                onClick={() => navigate("/pricing")}
                data-testid="nav-pricing-btn"
              >
                <CreditCard className="w-5 h-5 mr-3" />
                Buy Credits
                <span className="ml-auto text-xs tabular-nums px-1.5 py-0.5 rounded-md bg-muted text-muted-foreground">
                  {user?.credits || 0}
                </span>
              </Button>
              <Button
                variant="ghost"
                className="w-full justify-start h-12 text-base"
                onClick={() => navigate("/settings")}
                data-testid="nav-settings-btn"
              >
                <User className="w-5 h-5 mr-3" />
                Profile
              </Button>
            </>
          )}
        </nav>

        {/* Category Filters */}
        <div className="px-4 pb-4 border-t border-border pt-4">
          <div className="flex items-center justify-between mb-3">
            <p className="text-sm font-medium text-foreground flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Filter by Category
            </p>
            <button
              className="text-xs text-primary hover:underline"
              onClick={toggleAllCategories}
              data-testid="sidebar-filter-toggle-all"
            >
              {activeCategories.size === ALL_FILTER_CATEGORIES.length ? "Clear all" : "Select all"}
            </button>
          </div>
          <div className="space-y-1.5" data-testid="sidebar-category-filters">
            {user && (
              <button
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition-all ${
                  myPlacesFilter
                    ? "bg-muted text-foreground font-medium"
                    : "text-muted-foreground hover:bg-muted/50 opacity-50"
                }`}
                onClick={() => {
                  setMyPlacesFilter(prev => {
                    if (!prev) setFitToMarkersTrigger(t => t + 1);
                    return !prev;
                  });
                }}
                data-testid="sidebar-filter-my-places"
              >
                <div className={`w-7 h-7 rounded-lg ${myPlacesFilter ? "bg-rose-500" : "bg-muted"} flex items-center justify-center`}>
                  <Heart className={`w-4 h-4 ${myPlacesFilter ? "text-white fill-current" : "text-muted-foreground"}`} />
                </div>
                <span className="flex-1 text-left">My Places</span>
                {myPlacesFilter && <div className="w-2 h-2 rounded-full bg-rose-500" />}
              </button>
            )}
            {Object.entries(CATEGORY_CONFIG).filter(([k]) => k !== "cultural").map(([key, cfg]) => {
              const Icon = cfg.icon;
              const active = activeCategories.has(key);
              return (
                <button
                  key={key}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-xl text-sm transition-all ${
                    active
                      ? "bg-muted text-foreground font-medium"
                      : "text-muted-foreground hover:bg-muted/50 opacity-50"
                  }`}
                  onClick={() => toggleCategory(key)}
                  data-testid={`sidebar-filter-${key}`}
                >
                  <div className={`w-7 h-7 rounded-lg ${active ? cfg.color : "bg-muted"} flex items-center justify-center`}>
                    <Icon className={`w-4 h-4 ${active ? "text-white" : "text-muted-foreground"}`} />
                  </div>
                  <span className="flex-1 text-left">{cfg.label}</span>
                  {active && <div className="w-2 h-2 rounded-full bg-primary" />}
                </button>
              );
            })}
          </div>
        </div>
        
        <div className="p-4 border-t border-border">
          {user ? (
            <div className="flex items-center gap-3" data-testid="desktop-sidebar-user-info">
              <UserAvatar user={user} size="sm" />
              <div className="flex-1 min-w-0">
                <p className="font-medium text-foreground truncate">{user.name || user.email?.split("@")[0]}</p>
                <p className="text-sm text-muted-foreground">{user.credits || 0} credits</p>
              </div>
            </div>
          ) : (
            <div className="space-y-2">
              <Button
                className="w-full h-12 rounded-xl"
                onClick={() => navigate("/login?redirect=/explore")}
                data-testid="sidebar-login-btn"
              >
                <LogIn className="w-5 h-5 mr-2" />
                Sign In
              </Button>
              <Button
                variant="outline"
                className="w-full h-10 rounded-xl text-sm"
                onClick={() => navigate("/signup?redirect=/explore")}
                data-testid="sidebar-signup-btn"
              >
                Create Account
              </Button>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Sidebar Sheet */}
      <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
        <SheetContent side="left" className="w-64 p-0" hideClose>
          <SheetHeader className="p-5 border-b border-border">
            <button
              className="flex items-center gap-2 cursor-pointer"
              onClick={() => { setSidebarOpen(false); navigate("/"); }}
              data-testid="sidebar-home-link"
            >
              <img 
                src="/icon-192.png" 
                alt="Culture Ken"
                className="w-10 h-10 rounded-xl"
              />
              <SheetTitle className="text-xl font-bold">
                Culture Ken
              </SheetTitle>
            </button>
          </SheetHeader>
          
          <nav className="p-4 space-y-2">
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-base"
              onClick={() => { setSidebarOpen(false); navigate("/explore"); }}
            >
              <MapPin className="w-5 h-5 mr-3" />
              Explore
            </Button>
            <Button
              variant="ghost"
              className="w-full justify-start h-12 text-base"
              onClick={() => { setSidebarOpen(false); sessionStorage.removeItem("cv_tour_last"); navigate("/plan-tour"); }}
              data-testid="mobile-nav-plan-tour"
            >
              <Compass className="w-5 h-5 mr-3" />
              Plan a Trip
            </Button>
            {user && (
              <>
                <Button
                  variant="ghost"
                  className="w-full justify-start h-12 text-base"
                  onClick={() => { setSidebarOpen(false); navigate("/dashboard"); }}
                >
                  <Library className="w-5 h-5 mr-3" />
                  Library
                </Button>
                <Button
                  variant="ghost"
                  className="w-full justify-start h-12 text-base"
                  onClick={() => { setSidebarOpen(false); navigate("/pricing"); }}
                >
                  <CreditCard className="w-5 h-5 mr-3" />
                  Buy Credits
                </Button>
                <Button
                  variant="ghost"
                  className="w-full justify-start h-12 text-base"
                  onClick={() => { setSidebarOpen(false); navigate("/settings"); }}
                >
                  <Settings className="w-5 h-5 mr-3" />
                  Profile
                </Button>
              </>
            )}
          </nav>
          
          <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-border">
            {user ? (
              <div className="flex items-center gap-3" data-testid="sidebar-user-info">
                <UserAvatar user={user} size="sm" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-foreground truncate">{user.name}</p>
                  <p className="text-sm text-muted-foreground">{user.credits || 0} credits</p>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Button
                  className="w-full h-12 rounded-xl"
                  onClick={() => { setSidebarOpen(false); navigate("/login?redirect=/explore"); }}
                  data-testid="mobile-login-btn"
                >
                  <LogIn className="w-5 h-5 mr-2" />
                  Sign In
                </Button>
                <Button
                  variant="outline"
                  className="w-full h-10 rounded-xl text-sm"
                  onClick={() => { setSidebarOpen(false); navigate("/signup?redirect=/explore"); }}
                  data-testid="mobile-signup-btn"
                >
                  Create Account
                </Button>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>

      {/* Place Details Panel - Compact Mobile Design */}
      <Sheet open={detailsOpen && !!selectedMarker} onOpenChange={(open) => { if (!open) { setDetailsOpen(false); setSelectedMarker(null); } }}>
        <SheetContent 
          side="bottom" 
          className="rounded-t-3xl p-0 max-h-[45vh] min-h-[180px] overflow-y-auto"
        >
          <SheetTitle className="sr-only">Selected Location</SheetTitle>
          {/* Drag Handle */}
          <div className="w-10 h-1 bg-muted-foreground/30 rounded-full mx-auto mt-2 mb-2" />
          
          {selectedMarker ? (
            <div className="px-4 pb-4">
              {/* Compact Header: Image + Info */}
              <div className="flex gap-3">
                {/* Thumbnail Image */}
                {selectedMarker.image_url ? (
                  <div className="relative w-20 h-20 rounded-xl overflow-hidden flex-shrink-0 bg-[#ecebe8] dark:bg-[#302f2d] border border-black/5 dark:border-white/5">
                    <img
                      src={resolveMediaUrl(selectedMarker.image_url)}
                      alt={selectedMarker.name}
                      className="w-full h-full object-cover"
                      onError={(event) => {
                        event.currentTarget.classList.add("hidden");
                        event.currentTarget.nextElementSibling?.classList.remove("hidden");
                      }}
                    />
                    <div className="hidden absolute inset-0 flex items-center justify-center">
                      {(() => {
                        const IconComp = CATEGORY_CONFIG[selectedMarker.category]?.icon || MapPin;
                        return <IconComp className="w-8 h-8 text-primary/70" />;
                      })()}
                    </div>
                  </div>
                ) : (
                  <div className="w-20 h-20 rounded-xl flex-shrink-0 flex items-center justify-center bg-[#ecebe8] dark:bg-[#302f2d] border border-black/5 dark:border-white/5">
                    {(() => {
                      const IconComp = CATEGORY_CONFIG[selectedMarker.category]?.icon || MapPin;
                      return <IconComp className="w-8 h-8 text-primary/70" />;
                    })()}
                  </div>
                )}
                
                {/* Info */}
                <div className="flex-1 min-w-0 py-1">
                  {/* Category Badge - Small */}
                  <Badge 
                    variant="secondary" 
                    className={`text-[10px] px-2 py-0 h-5 ${CATEGORY_CONFIG[selectedMarker.category]?.color || 'bg-primary'} text-white mb-1`}
                  >
                    {CATEGORY_CONFIG[selectedMarker.category]?.label || selectedMarker.category}
                  </Badge>
                  
                  {/* Name */}
                  <h3 className="font-bold text-lg leading-tight text-foreground truncate">
                    {selectedMarker.name}
                  </h3>
                  
                  {/* Address (fallback to description for old markers, but enriched via API) */}
                  {(selectedMarker.address || selectedMarker.description) && (
                    <p className="text-xs text-muted-foreground truncate mt-0.5 flex items-center gap-1" data-testid="place-address">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      {selectedMarker.address || selectedMarker.description}
                    </p>
                  )}
                  
                  {/* Google Star Rating */}
                  {selectedMarker.rating > 0 && (
                    <div className="flex items-center gap-1.5 mt-0.5" data-testid="place-rating">
                      <div className="flex items-center">
                        {[1, 2, 3, 4, 5].map(star => {
                          const fill = Math.min(1, Math.max(0, selectedMarker.rating - (star - 1)));
                          return (
                            <svg key={star} viewBox="0 0 20 20" className="w-3.5 h-3.5">
                              <defs><linearGradient id={`star-${star}-${selectedMarker.id}`}><stop offset={`${fill * 100}%`} stopColor="#FBBC04" /><stop offset={`${fill * 100}%`} stopColor="#D1D5DB" /></linearGradient></defs>
                              <path fill={`url(#star-${star}-${selectedMarker.id})`} d="M10 1l2.39 4.84 5.34.78-3.87 3.77.91 5.32L10 13.27l-4.77 2.51.91-5.32L2.27 6.69l5.34-.78L10 1z" />
                            </svg>
                          );
                        })}
                      </div>
                      <span className="text-xs font-medium text-foreground">{selectedMarker.rating}</span>
                      {selectedMarker.review_count > 0 && (
                        <span className="text-xs text-muted-foreground">({selectedMarker.review_count.toLocaleString()})</span>
                      )}
                    </div>
                  )}

                  {/* Short Description - click to expand (only shown when real editorial exists) */}
                  {(() => {
                    const addr = (selectedMarker.address || "").toLowerCase();
                    const desc = selectedMarker.description || "";
                    const descLower = desc.toLowerCase();
                    // Hide if empty or if it's just the address repeated
                    if (!desc || (addr && descLower === addr)) return null;
                    return (
                      <p
                        className={`text-sm text-muted-foreground mt-0.5 cursor-pointer ${descExpanded ? "" : "line-clamp-2"}`}
                        onClick={() => setDescExpanded(!descExpanded)}
                        data-testid="place-description"
                      >
                        {desc}
                      </p>
                    );
                  })()}
                </div>
              </div>
              
              {/* Buy Audio Button / Progress Bar */}
              <div className="mt-4">
                {loading && generationProgress ? (
                  /* Progress bar during generation */
                  <div className="space-y-3 p-4 bg-muted/50 rounded-2xl" data-testid="generation-progress">
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-medium text-foreground">{generationProgress.step}</span>
                      <span className="text-muted-foreground">{generationProgress.progress}%</span>
                    </div>
                    <Progress value={generationProgress.progress} className="h-3" />
                    <p className="text-xs text-muted-foreground text-center">
                      Preparing your audio guide...
                    </p>
                  </div>
                ) : ownershipCheckingPlaceId === selectedMarker.id ? (
                  <Button
                    className="w-full h-14 rounded-2xl text-base font-semibold"
                    disabled
                    data-testid="ownership-checking-btn"
                  >
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Checking library...
                  </Button>
                ) : !selectedMarkerOwned ? (
                  <Button
                    className="w-full h-14 rounded-2xl text-base font-semibold shadow-lg active:scale-[0.98] transition-transform"
                    onClick={handleGenerateSummary}
                    disabled={loading}
                    data-testid="buy-summary-btn"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Generating...
                      </>
                    ) : !user ? (
                      <div className="flex items-center justify-center w-full px-1">
                        <LogIn className="w-5 h-5 mr-2" />
                        <span>Sign in for Audio Guide</span>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center w-full px-1">
                        <Headphones className="w-5 h-5 mr-2" />
                        <span>Get Audio Guide</span>
                      </div>
                    )}
                  </Button>
                ) : (
                  <Button
                    className="w-full h-14 rounded-2xl text-base font-semibold shadow-lg active:scale-[0.98] transition-transform"
                    onClick={handlePlayAudio}
                    disabled={audioLoading}
                    data-testid="play-audio-btn"
                  >
                    {audioLoading ? (
                      <>
                        <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                        Loading Audio...
                      </>
                    ) : (
                      <div className="flex items-center justify-between w-full px-1">
                        <div className="flex items-center">
                          <Play className="w-5 h-5 mr-2" />
                          <span>Play Audio Guide</span>
                        </div>
                        <span className="text-sm opacity-90 bg-white/20 px-2 py-0.5 rounded-full">
                          {selectedMarker.audio_duration 
                            ? `${Math.floor(selectedMarker.audio_duration / 60)}:${String(selectedMarker.audio_duration % 60).padStart(2, '0')}`
                            : `~${Math.ceil((selectedMarker.summary?.split(' ').length || 300) / 150)} min`
                          }
                        </span>
                      </div>
                    )}
                  </Button>
                )}
              </div>
              
              {/* Expandable Summary (if exists) */}
              {selectedMarker.summary && (
                <details className="mt-3">
                  <summary className="text-sm text-primary cursor-pointer font-medium">
                    Read summary text
                  </summary>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">
                    {selectedMarker.summary}
                  </p>
                  {selectedMarker.wikipedia_url && (
                    <a
                      href={selectedMarker.wikipedia_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary text-sm hover:underline mt-2 inline-block"
                    >
                      Wikipedia →
                    </a>
                  )}
                </details>
              )}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Quick Buy Dialog */}
      <Dialog open={quickBuyOpen} onOpenChange={setQuickBuyOpen}>
        <DialogContent
          ref={quickBuyDialogRef}
          tabIndex={-1}
          className="sm:max-w-sm focus:outline-none"
          onOpenAutoFocus={(event) => {
            event.preventDefault();
            quickBuyDialogRef.current?.focus();
          }}
        >
          <DialogHeader>
            <DialogTitle className="text-xl">Get Audio Guide</DialogTitle>
            <DialogDescription>
              {selectedMarker?.name || "Selected place"}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="space-y-2">
              <label
                htmlFor="buy-audio-language"
                className="flex items-center gap-2 text-sm font-medium text-muted-foreground"
              >
                <Globe className="h-4 w-4" />
                Audio Language
              </label>
              <select
                id="buy-audio-language"
                value={purchaseLanguage}
                onChange={(event) => setPurchaseLanguage(event.target.value)}
                className="h-12 w-full rounded-xl border border-input bg-background px-3 text-base text-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
                data-testid="buy-language-select"
              >
                {supportedLanguages.map((language) => (
                  <option key={language.code} value={language.code}>
                    {language.label}
                  </option>
                ))}
              </select>
            </div>
            {user && (user.subscription_status === "active" || user.subscription_status === "cancelled") && user.subscription_end_date && new Date(user.subscription_end_date) > new Date() ? (
              /* Subscriber — unlimited access */
              <Button
                className="w-full h-14 rounded-xl text-base font-medium"
                onClick={handleUseCredit}
                disabled={loading}
                data-testid="subscriber-generate-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Headphones className="w-5 h-5 mr-2" />
                    Get Audio Guide
                  </>
                )}
              </Button>
            ) : user && user.credits > 0 ? (
              /* User HAS credits */
              <Button
                className="w-full h-14 rounded-xl text-base font-medium"
                onClick={handleUseCredit}
                disabled={loading}
                data-testid="use-credit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Headphones className="w-5 h-5 mr-2" />
                    Use 1 credit
                  </>
                )}
              </Button>
            ) : (
              /* User has NO credits */
              <Button
                className="w-full h-14 rounded-xl text-base font-medium"
                onClick={() => {
                  setQuickBuyOpen(false);
                  navigate("/pricing");
                }}
                data-testid="buy-credits-btn"
              >
                <CreditCard className="w-5 h-5 mr-2" />
                Buy credits
              </Button>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Bottom Navigation (Mobile) - Larger touch targets */}
      <nav className="lg:hidden mobile-nav">
        <div className="flex items-center justify-around">
          <button
            className="mobile-nav-item active"
            onClick={() => navigate("/explore")}
            data-testid="bottom-nav-explore"
          >
            <MapPin className="w-7 h-7" />
            <span className="text-xs font-semibold">Explore</span>
          </button>
          {user ? (
            <>
              <button
                className="mobile-nav-item text-muted-foreground"
                onClick={() => navigate("/plan-tour")}
                data-testid="bottom-nav-plan-tour"
              >
                <Navigation className="w-7 h-7" />
                <span className="text-xs font-medium">Plan</span>
              </button>
              <button
                className="mobile-nav-item text-muted-foreground"
                onClick={() => navigate("/dashboard")}
                data-testid="bottom-nav-dashboard"
              >
                <Library className="w-7 h-7" />
                <span className="text-xs font-medium">Library</span>
              </button>
              <button
                className="mobile-nav-item text-muted-foreground"
                onClick={() => navigate("/pricing")}
                data-testid="bottom-nav-credits"
              >
                <CreditCard className="w-7 h-7" />
                <span className="text-xs font-medium">{user.credits || 0}</span>
              </button>
              <button
                className="mobile-nav-item text-muted-foreground"
                onClick={() => navigate("/settings")}
                data-testid="bottom-nav-settings"
              >
                <User className="w-7 h-7" />
                <span className="text-xs font-medium">Profile</span>
              </button>
            </>
          ) : authLoading ? (
            // While auth session is still being restored, don't flash the
            // signed-out "Sign In" state — show placeholder pills so the nav
            // doesn't feel broken on Safari (where restore takes a network hop).
            <>
              {[0, 1, 2, 3].map((i) => (
                <div key={i} className="mobile-nav-item opacity-30" data-testid={`bottom-nav-skeleton-${i}`}>
                  <div className="w-7 h-7 rounded-full bg-muted animate-pulse" />
                  <span className="text-xs">&nbsp;</span>
                </div>
              ))}
            </>
          ) : (
            <button
              className="mobile-nav-item text-muted-foreground"
              onClick={() => navigate("/login?redirect=/explore")}
              data-testid="bottom-nav-signin"
            >
              <LogIn className="w-7 h-7" />
              <span className="text-xs font-medium">Sign In</span>
            </button>
          )}
        </div>
      </nav>

      {/* One-time location access dialog for new users */}
      <Dialog open={showLocationPrompt} onOpenChange={(open) => {
        if (!open) {
          localStorage.removeItem("needs_location_onboarding");
          setShowLocationPrompt(false);
          requestLocation(false);
        }
      }}>
        <DialogContent className="max-w-sm mx-auto" data-testid="location-onboarding-dialog">
          <DialogHeader className="text-center items-center">
            <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-2">
              <Navigation className="w-8 h-8 text-primary" />
            </div>
            <DialogTitle className="text-lg">Enable Location Access</DialogTitle>
            <DialogDescription className="text-sm text-muted-foreground">
              Allow Culture Ken to use your location to find points of interest near you and centre the map on your position.
            </DialogDescription>
          </DialogHeader>
          <div className="flex flex-col gap-2 mt-2">
            <Button
              className="w-full rounded-xl h-11"
              onClick={() => {
                localStorage.removeItem("needs_location_onboarding");
                setShowLocationPrompt(false);
                requestLocation(true);
              }}
              data-testid="enable-location-btn"
            >
              <Navigation className="w-4 h-4 mr-2" />
              Enable Location
            </Button>
            <Button
              variant="ghost"
              className="w-full rounded-xl h-11 text-muted-foreground"
              onClick={() => {
                localStorage.removeItem("needs_location_onboarding");
                setShowLocationPrompt(false);
                requestLocation(false);
              }}
              data-testid="skip-location-btn"
            >
              Not Now
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MapPage;
